import React from "react";
import LocalizedLink from "@/components/LocalizedLink";
import { useTranslation } from "react-i18next";
import useScrollAnimation from "@/hooks/useScrollAnimation";
import { PACKAGES } from "@/data/packages";
import { useContact } from "@/context/ContactContext";
import { trackEvent } from "@/utils/analytics";
import "./PackagesSection.scss";

const PackagesSection: React.FC = () => {
  const { t } = useTranslation();
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 });
  const { requestContact } = useContact();

  const items = PACKAGES.map((pkg) => ({
    pkg,
    content: t(`packages.items.${pkg.key}`, { returnObjects: true }) as {
      name: string;
      tagline: string;
      priceBand: string;
      priceNote: string;
      timeline: string;
      includes: string[];
      ctaLabel: string;
    },
  }));

  return (
    <section className="packages-section" id="packages-section" ref={ref}>
      <div className="container">
        <div className={`pkg-head reveal${isVisible ? " in" : ""}`}>
          <span className="sec-label">{t("packages.label")}</span>
          <h2 className="sec-title">
            {(() => {
              const title = t("packages.title");
              const hl = t("packages.titleHighlight");
              const [before, after = ""] = title.split(hl);
              return (
                <>
                  {before}
                  <em>{hl}</em>
                  {after}
                </>
              );
            })()}
          </h2>
          <p className="pkg-subtitle">{t("packages.subtitle")}</p>
        </div>

        <ul className="pkg-list" aria-label={t("packages.label")}>
          {items.map(({ pkg, content }, i) => (
            <li
              key={pkg.slug}
              className={`pkg-card reveal${isVisible ? " in" : ""}${pkg.featured ? " pkg-card--featured" : ""}`}
              style={{ transitionDelay: `${0.1 + i * 0.08}s` }}
            >
              {pkg.featured && (
                <span className="pkg-badge">{t("packages.featuredBadge")}</span>
              )}
              <h3 className="pkg-name">{content.name}</h3>
              <p className="pkg-tagline">{content.tagline}</p>

              <div className="pkg-meta">
                <span className="pkg-price">{content.priceBand}</span>
                <span className="pkg-price-note">{content.priceNote}</span>
              </div>

              <div className="pkg-timeline">
                <span className="pkg-timeline-label">{content.timeline}</span>
              </div>

              <ul className="pkg-includes">
                {content.includes.slice(0, 3).map((item, j) => (
                  <li key={j}>{item}</li>
                ))}
              </ul>

              <div className="pkg-actions">
                <LocalizedLink
                  to={`/packages/${pkg.slug}`}
                  className="pkg-btn pkg-btn--secondary"
                  onClick={() =>
                    trackEvent("package_details_open", { package: pkg.slug })
                  }
                >
                  {t("packages.seeDetails")}
                </LocalizedLink>
                <button
                  className="pkg-btn pkg-btn--primary"
                  onClick={() => {
                    trackEvent("package_card_cta", { package: pkg.slug });
                    requestContact(content.ctaLabel);
                  }}
                >
                  {t("packages.bookCta")}
                </button>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

export default PackagesSection;
